import {
    ClientStatus,
    IArchivedClient,
    IClient,
    RtdClientChangesNotification,
} from "intake_shared/src/types";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoute } from "../../../../navigation/routes";
import { AclContext } from "../../../../contexts/AclContext";
import { getClientChangesNotificationRef } from "../../../../realtimedatabase/notification";
import { onChildAdded } from "firebase/database";
import { uniqBy } from "lodash";
import styles from "./InactiveClientMasterView.module.scss";
import { Button } from "../../bits/Button/Button";
import ChevronBackIcon from "../../icons/ChevronBack";
import ClientInfo from "../ClientInfo/ClientInfo";
import EditClient from "../client-forms/edit-client/EditClient/EditClient";
import HistoryIcon from "../../icons/History";
import ClientChangeHistoryNotification from "../ClientChangeHistoryNotification/ClientChangeHistoryNotification";
import store from "../../../../store";
import queryString from "query-string";
import Loader from "../../bits/Loader/Loader";
import {
    ClientListReturnState,
    getClientListReturnLocation,
} from "../client_list_navigation";

export default function InactiveClientMasterView() {
    const history = useHistory();
    const aclContext = React.useContext(AclContext);
    const [notificationStartTime] = React.useState<number>(Date.now());
    const browserLocation = useLocation();
    const parsedQueryParams = queryString.parse(browserLocation.search || "");
    const [clientUrlId] = React.useState(
        (parsedQueryParams["clientId"] || "") as string
    );
    const [inactiveClient, setInactiveClient] = React.useState(
        (browserLocation.state || {}) as ClientListReturnState & {
            client: IArchivedClient;
        }
    );
    const [isLoading, setIsLoading] = React.useState(false);

    const replaceWithReactivatedClient = React.useCallback(
        (reactivatedClient: IClient) => {
            history.replace({
                pathname: `/v2${AppRoute.CLIENT_FULL_VIEW}`,
                search: `?clientId=${reactivatedClient.id}&activeTab=${reactivatedClient.status}`,
                state: {
                    client: reactivatedClient,
                    returnPathname: inactiveClient.returnPathname,
                    returnSearch: inactiveClient.returnSearch,
                    returnState: inactiveClient.returnState,
                },
            });
        },
        [
            history,
            inactiveClient.returnPathname,
            inactiveClient.returnSearch,
            inactiveClient.returnState,
        ]
    );

    const [
        showClientChangeNotificationHistory,
        setShowClientChangeNotificationHistory,
    ] = React.useState(false);

    const showClientChangeHistory = async () => {
        setShowClientChangeNotificationHistory(true);
    };
    const [clientChangeNotificationHistory, setClientChangeNotificationHistory] =
        React.useState<RtdClientChangesNotification[]>([]);

    const client = inactiveClient.client;
    const client_id = client?.id || clientUrlId;

    if (!client_id) {
        history.push("/v2/clients");
    }

    const triggerRefresh = React.useCallback(async () => {
        if (!client_id) {
            return;
        }
        setIsLoading(true);
        try {
            const updatedClient = await store.getClientHistoryById(client_id);
            if (!updatedClient) {
                const reactivatedClient = await store.getClientById(client_id);
                if (reactivatedClient) {
                    replaceWithReactivatedClient(reactivatedClient);
                }
                return;
            }
            setInactiveClient((prevInactiveClient) => {
                return {
                    ...prevInactiveClient,
                    client: updatedClient,
                };
            });
        } finally {
            setIsLoading(false);
        }
    }, [client_id, replaceWithReactivatedClient]);

    React.useEffect(() => {
        triggerRefresh();
    }, [triggerRefresh]);

    React.useEffect(() => {
        if (!client) {
            return;
        }
        // Listen for client notification events specifically for this client.
        const clientNotificationRef = getClientChangesNotificationRef(client.id);
        onChildAdded(clientNotificationRef, (snapshot) => {
            const notification: RtdClientChangesNotification = snapshot.val();
            setClientChangeNotificationHistory(
                (prevClientChangeNotificationHistory) => {
                    return uniqBy(
                        [...prevClientChangeNotificationHistory, notification],
                        "id"
                    );
                }
            );
        });
    }, [client, aclContext, notificationStartTime]);

    if (isLoading) {
        return <Loader show={isLoading} />;
    }

    return client && client.id ? (
        <div className="v2-page">
            {showClientChangeNotificationHistory && (
                <ClientChangeHistoryNotification
                    key={client.id}
                    clientId={client.id}
                    notifications={clientChangeNotificationHistory}
                    firstName={client?.first_name}
                    showPopup={showClientChangeNotificationHistory}
                    onClose={() => setShowClientChangeNotificationHistory(false)}
                />
            )}
            <div className={styles.headerWrapper}>
                <div className={styles.headerLeft}>
                    <div>
                        <Button
                            onClick={() => {
                                if (inactiveClient.returnPathname) {
                                    history.push(
                                        getClientListReturnLocation(inactiveClient, {
                                            pathname: "/v2/client-master-list",
                                        })
                                    );
                                    return;
                                }

                                history.goBack();
                            }}
                            variant="normal"
                            size="small"
                            className={styles.backButton}
                        >
                            <ChevronBackIcon />
                        </Button>
                    </div>

                    <ClientInfo
                        client={client}
                        acl={aclContext?.userAcl}
                        isInactive={true}
                        onSaveClient={async (client) => {
                            const updatedClient = await store.getClientHistoryById(
                                client.id
                            );
                            if (!updatedClient) {
                                const reactivatedClient = await store.getClientById(
                                    client.id
                                );
                                if (reactivatedClient) {
                                    replaceWithReactivatedClient(reactivatedClient);
                                }
                                return;
                            }
                            setInactiveClient((currentInactiveClient) => ({
                                ...currentInactiveClient,
                                client: updatedClient as IArchivedClient,
                            }));
                        }}
                    />
                </div>
                <div>
                    <Button variant="normal" onClick={showClientChangeHistory}>
                        <HistoryIcon />
                    </Button>
                </div>
            </div>
            <EditClient
                onSaveClient={async (client) => {
                    const updatedClient = await store.getClientHistoryById(
                        client.id
                    );
                    if (!updatedClient) {
                        const reactivatedClient = await store.getClientById(
                            client.id
                        );
                        if (reactivatedClient) {
                            replaceWithReactivatedClient(reactivatedClient);
                        }
                        return;
                    }
                    setInactiveClient((currentInactiveClient) => ({
                        ...currentInactiveClient,
                        client: updatedClient as IArchivedClient,
                    }));
                }}
                clientProp={client}
                activeTab={ClientStatus.INQUIRY}
                isInactiveClient={true}
            />
        </div>
    ) : (
        <div></div>
    );
}
